// src/components/ActivityFeed.tsx
import ActivityFeedPost from "@/components/conduction-social/ActivityFeedPost";
import { SessionContext } from "@/context/SessionContext";
import ConductionAPI, {
    ActivityFeedItem,
    ActivityFeedItemKind,
    PagedResults,
} from "@/javascript/conduction-api";
import {
    Box,
    Card,
    Loader,
    ScrollArea,
    Skeleton,
    Stack,
    Text,
    useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

type ActivityFeedProps = {
    classroomId?: string;
    userId?: string;
    goToUserFeed: (userId: string) => void;
};

const PAGE_SIZE = 4;

const ActivityFeed: React.FC<ActivityFeedProps> = ({ classroomId, userId, goToUserFeed }) => {
    const session = useContext(SessionContext);
    if (!session) throw new Error("ActivityFeed must be a child of SessionContext.");
    const token = session.token;

    const theme = useMantineTheme();
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

    const [items, setItems] = useState<ActivityFeedItem[]>([]);
    const [page, setPage] = useState(1);
    const [hasNext, setHasNext] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const seenKeys = useRef<Set<string>>(new Set());

    const keyForItem = useCallback((it: ActivityFeedItem): string => {
        if (it.kind === ActivityFeedItemKind.Submission && it.submission)
            return `sub:${it.submission.id}`;
        if (it.kind === ActivityFeedItemKind.Publication && it.publication)
            return `pub:${it.publication.id}`;
        return `k:${(it as any)?.eventDate ?? Math.random().toString(36)}`;
    }, []);

    const resetAndLoad = useCallback(async () => {
        setInitialLoading(true);
        setLoadingMore(false);
        setError(null);
        setItems([]);
        setPage(1);
        setHasNext(true);
        seenKeys.current.clear();

        try {
            const result = await ConductionAPI.getActivityFeedPage(
                token!,
                1,
                PAGE_SIZE,
                classroomId,
                userId
            );
            const deduped = result.items.filter((it) => {
                const k = keyForItem(it);
                if (seenKeys.current.has(k)) return false;
                seenKeys.current.add(k);
                return true;
            });
            setItems(deduped);
            setHasNext(result.hasNext);
            setPage(2);
        } catch (e: any) {
            setError(e?.message ?? "Failed to load activity feed.");
        } finally {
            setInitialLoading(false);
        }
    }, [classroomId, userId, keyForItem, token]);

    const loadNext = useCallback(async () => {
        if (loadingMore || initialLoading || !hasNext) return;
        setLoadingMore(true);
        setError(null);
        try {
            const result: PagedResults<ActivityFeedItem> =
                await ConductionAPI.getActivityFeedPage(
                    token!,
                    page,
                    PAGE_SIZE,
                    classroomId,
                    userId
                );

            const next = result.items.filter((it) => {
                const k = keyForItem(it);
                if (seenKeys.current.has(k)) return false;
                seenKeys.current.add(k);
                return true;
            });

            setItems((prev) => [...prev, ...next]);
            setHasNext(result.hasNext);
            setPage((prev) => prev + 1);
        } catch (e: any) {
            setError(e?.message ?? "Failed to load more activity.");
        } finally {
            setLoadingMore(false);
        }
    }, [classroomId, userId, hasNext, initialLoading, keyForItem, loadingMore, page, token]);

    useEffect(() => {
        if (token) void resetAndLoad();
    }, [resetAndLoad, token]);

    // Infinite scroll
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) void loadNext();
            },
            { root: null, rootMargin: "400px", threshold: 0 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [loadNext]);

    const CARD_SKELETON_MAX_WIDTH = isMobile ? 360 : 1080;
    const SkeletonBlock = useMemo(
        () => (
            <Card
                withBorder
                radius="sm"
                p="md"
                style={{ width: "100%", maxWidth: CARD_SKELETON_MAX_WIDTH, minHeight: 300 }}
            >
                <Stack gap="sm">
                    <Skeleton height={16} width="70%" />
                    <Skeleton height={16} width="40%" />
                    <Skeleton height={isMobile ? 220 : 340} />
                </Stack>
            </Card>
        ),
        [CARD_SKELETON_MAX_WIDTH, isMobile]
    );

    const FEED_MAX_WIDTH = isMobile ? "100%" : 1000;

    return (
        <ScrollArea
            style={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
            }}
            styles={{ content: { display: "flex", justifyContent: "center" } }}
        >
            <Stack align="center" gap="md" w="100%" maw={FEED_MAX_WIDTH} p="xl">
                {error && (
                    <Card
                        withBorder
                        radius="sm"
                        p="md"
                        style={{ width: "100%", maxWidth: CARD_SKELETON_MAX_WIDTH }}
                    >
                        <Text c={theme.colors.red[7]} fw={600}>
                            {error}
                        </Text>
                    </Card>
                )}

                {initialLoading &&
                    Array.from({ length: PAGE_SIZE }).map((_, i) => (
                        <React.Fragment key={`sk-${i}`}>{SkeletonBlock}</React.Fragment>
                    ))}

                {!initialLoading &&
                    items.map((it) => {
                        if (it.kind === ActivityFeedItemKind.Submission && it.submission) {
                            return (
                                <div
                                    key={`sub-${it.submission.id}`}
                                    style={{ width: "100%", display: "flex", justifyContent: "center" }}
                                >
                                    <ActivityFeedPost submission={it.submission} goToUserFeed={goToUserFeed} />
                                </div>
                            );
                        }
                        if (it.kind === ActivityFeedItemKind.Publication && it.publication) {
                            return (
                                <div
                                    key={`pub-${it.publication.id}`}
                                    style={{ width: "100%", display: "flex", justifyContent: "center" }}
                                >
                                    <ActivityFeedPost publication={it.publication} goToUserFeed={goToUserFeed} />
                                </div>
                            );
                        }
                        return (
                            <Card
                                key={Math.random()}
                                withBorder
                                radius="sm"
                                p="md"
                                style={{ width: "100%", maxWidth: CARD_SKELETON_MAX_WIDTH }}
                            >
                                <Text>Unknown feed item</Text>
                            </Card>
                        );
                    })}

                {loadingMore &&
                    Array.from({ length: 2 }).map((_, i) => (
                        <React.Fragment key={`lm-${i}`}>{SkeletonBlock}</React.Fragment>
                    ))}

                {!initialLoading && !loadingMore && !hasNext && (
                    <Card
                        withBorder
                        radius="sm"
                        p="lg"
                        style={{ width: "100%", maxWidth: CARD_SKELETON_MAX_WIDTH, textAlign: "center" }}
                    >
                        <Stack gap="xs" align="center">
                            <Text fw={700} fz="lg">
                                You have reached the end of the activity feed.
                            </Text>
                            <Text c="dimmed">
                                You can contribute to the feed by sending projects to musicians or sharing a project for free!
                            </Text>
                        </Stack>
                    </Card>
                )}

                <Box ref={sentinelRef} style={{ height: 1 }} />
                {loadingMore && <Loader />}
            </Stack>
        </ScrollArea>
    );
};

export default ActivityFeed;
